/* ============================================
   ImportarProspectos — de la planilla del SDR a la lista

   Tres pasos: subir el archivo, decir qué columna es
   qué, y confirmar. El de en medio existe porque la
   planilla la escribe una persona: los títulos cambian
   de lista en lista y adivinarlos siempre sale mal
   alguna vez. Se sugiere el mapeo y se deja corregir.

   Antes de escribir nada se pregunta al backend qué
   pasaría: cuántos entran y cuáles ya existen.
   ============================================ */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { Modal } from '@/components/ui'
import { IconUpload, IconCheckCircle, IconAlertTriangle } from '@/components/icons/Icons'

interface ImportarProspectosProps {
  open: boolean
  onClose: () => void
  /** Se llama cuando la importación escribió algo, para recargar la lista. */
  onImportado: () => void
}

type Campo = 'empresa' | 'ciudad' | 'industria' | 'senal_dolor' | 'servicio_interes' | 'prioridad' | 'contacto'

const CAMPOS: { key: Campo; label: string; requerido?: boolean }[] = [
  { key: 'empresa', label: 'Empresa', requerido: true },
  { key: 'ciudad', label: 'Ciudad' },
  { key: 'industria', label: 'Industria' },
  { key: 'prioridad', label: 'Prioridad (A/B/C)' },
  { key: 'servicio_interes', label: 'Servicio a ofrecer' },
  { key: 'senal_dolor', label: 'Señal de dolor' },
  { key: 'contacto', label: 'Contacto (LinkedIn, web, persona)' },
]

interface Planilla {
  columnas: string[]
  filas: string[][]
  total: number
  mapeo_sugerido: Record<Campo, number | null>
  hoja: string
  truncado: boolean
}

interface Diagnostico {
  creados: number
  a_crear?: number
  duplicados: { empresa: string; codigo: string; etapa: string }[]
  confirmado: boolean
}

const SIN_MAPEAR = -1

export default function ImportarProspectos({ open, onClose, onImportado }: ImportarProspectosProps) {
  const api = useApi()
  const inputRef = useRef<HTMLInputElement>(null)

  const [planilla, setPlanilla] = useState<Planilla | null>(null)
  const [mapeo, setMapeo] = useState<Record<Campo, number>>({} as Record<Campo, number>)
  const [subiendo, setSubiendo] = useState(false)
  const [revisando, setRevisando] = useState(false)
  const [importando, setImportando] = useState(false)
  const [diagnostico, setDiagnostico] = useState<Diagnostico | null>(null)
  const [resultado, setResultado] = useState<Diagnostico | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [arrastrando, setArrastrando] = useState(false)

  function reiniciar() {
    setPlanilla(null); setDiagnostico(null); setResultado(null); setError(null)
    setMapeo({} as Record<Campo, number>)
    if (inputRef.current) inputRef.current.value = ''
  }

  function cerrar() {
    if (resultado?.creados) onImportado()
    reiniciar()
    onClose()
  }

  async function leerArchivo(archivo: File | null | undefined) {
    if (!archivo) return
    setError(null)
    setSubiendo(true)
    try {
      const fd = new FormData()
      fd.append('file', archivo)
      const data = await api.upload<Planilla>('/api/leads/prospectos/archivo', fd)
      setPlanilla(data)
      const inicial = {} as Record<Campo, number>
      for (const c of CAMPOS) inicial[c.key] = data.mapeo_sugerido?.[c.key] ?? SIN_MAPEAR
      setMapeo(inicial)
    } catch (err: any) {
      console.error('Error leyendo planilla:', err)
      setError(err?.message || 'No se pudo leer el archivo')
    } finally {
      setSubiendo(false)
    }
  }

  /** Convierte las filas crudas al payload del backend, según el mapeo actual. */
  const filasMapeadas = useMemo(() => {
    if (!planilla || mapeo.empresa === undefined || mapeo.empresa === SIN_MAPEAR) return []
    const valor = (fila: string[], campo: Campo) => {
      const i = mapeo[campo]
      return i === undefined || i === SIN_MAPEAR ? null : (fila[i] || null)
    }
    return planilla.filas
      .map(fila => ({
        empresa: (fila[mapeo.empresa] || '').trim(),
        ciudad: valor(fila, 'ciudad'),
        industria: valor(fila, 'industria'),
        senal_dolor: valor(fila, 'senal_dolor'),
        servicio_interes: valor(fila, 'servicio_interes'),
        prioridad: valor(fila, 'prioridad'),
        contacto: valor(fila, 'contacto'),
      }))
      .filter(f => f.empresa !== '')
  }, [planilla, mapeo])

  const revisar = useCallback(async () => {
    if (filasMapeadas.length === 0) { setDiagnostico(null); return }
    setRevisando(true)
    setError(null)
    try {
      setDiagnostico(await api.post<Diagnostico>('/api/leads/prospectos/importar', {
        filas: filasMapeadas, confirmar: false,
      }))
    } catch (err: any) {
      console.error('Error revisando prospectos:', err)
      setError(err?.message || 'No se pudo revisar la planilla')
      setDiagnostico(null)
    } finally {
      setRevisando(false)
    }
  }, [api, filasMapeadas])

  // Cada cambio de mapeo vuelve a preguntar qué pasaría: el número de la
  // derecha tiene que corresponderse con lo que se ve en la tabla.
  useEffect(() => {
    if (!planilla) return
    const t = setTimeout(revisar, 250)
    return () => clearTimeout(t)
  }, [planilla, revisar])

  async function importar() {
    setImportando(true)
    setError(null)
    try {
      const res = await api.post<Diagnostico>('/api/leads/prospectos/importar', {
        filas: filasMapeadas, confirmar: true,
      })
      setResultado(res)
    } catch (err: any) {
      console.error('Error importando prospectos:', err)
      setError(err?.message || 'No se pudo importar')
    } finally {
      setImportando(false)
    }
  }

  const aCrear = diagnostico?.a_crear ?? 0
  const muestra = filasMapeadas.slice(0, 4)

  return (
    <Modal
      open={open}
      onClose={cerrar}
      title="Importar prospectos"
      width={760}
      footer={
        resultado ? (
          <button className="btn btn-primary" onClick={cerrar}>Listo</button>
        ) : planilla ? (
          <>
            <button className="btn btn-secondary" onClick={reiniciar}>Elegir otro archivo</button>
            <button
              className="btn btn-primary"
              onClick={importar}
              disabled={importando || revisando || aCrear === 0}
            >
              {importando ? 'Importando...' : `Importar ${aCrear} prospecto${aCrear !== 1 ? 's' : ''}`}
            </button>
          </>
        ) : (
          <button className="btn btn-secondary" onClick={cerrar}>Cancelar</button>
        )
      }
    >
      {error && (
        <p className="importar__error"><IconAlertTriangle size={14} /> {error}</p>
      )}

      {/* ─── Paso 1: el archivo ─── */}
      {!planilla && !resultado && (
        <div
          className={`importar__zona${arrastrando ? ' importar__zona--activa' : ''}`}
          onDragOver={e => { e.preventDefault(); setArrastrando(true) }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={e => { e.preventDefault(); setArrastrando(false); leerArchivo(e.dataTransfer.files?.[0]) }}
        >
          <IconUpload size={26} />
          <p className="importar__zona-titulo">
            {subiendo ? 'Leyendo la planilla...' : 'Arrastrá la planilla o elegila'}
          </p>
          <p className="importar__zona-hint">.xlsx o .csv · hasta 5 MB · máximo 500 filas</p>
          <button className="btn btn-secondary btn-sm" onClick={() => inputRef.current?.click()} disabled={subiendo}>
            Elegir archivo
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xlsm,.csv"
            className="importar__input"
            onChange={e => leerArchivo(e.target.files?.[0])}
          />
        </div>
      )}

      {/* ─── Paso 2: el mapeo ─── */}
      {planilla && !resultado && (
        <>
          <p className="importar__resumen">
            Hoja <strong>{planilla.hoja}</strong> · {planilla.total} fila{planilla.total !== 1 ? 's' : ''}
            {planilla.truncado && ' (se leyeron las primeras 500)'}
          </p>

          <div className="importar__mapeo">
            {CAMPOS.map(campo => (
              <label key={campo.key} className="importar__campo">
                <span className="importar__campo-label">
                  {campo.label}{campo.requerido && <span className="importar__req"> *</span>}
                </span>
                <select
                  className="form-input"
                  value={mapeo[campo.key] ?? SIN_MAPEAR}
                  onChange={e => setMapeo(prev => ({ ...prev, [campo.key]: Number(e.target.value) }))}
                >
                  <option value={SIN_MAPEAR}>— sin usar —</option>
                  {planilla.columnas.map((col, i) => (
                    <option key={i} value={i}>{col}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {mapeo.empresa === SIN_MAPEAR ? (
            <p className="importar__aviso">
              <IconAlertTriangle size={14} /> Elegí qué columna trae el nombre de la empresa:
              es con lo que se deduplica.
            </p>
          ) : (
            <>
              <div className="importar__preview">
                <table>
                  <thead>
                    <tr><th>Empresa</th><th>Ciudad</th><th>Prio.</th><th>Contacto detectado</th></tr>
                  </thead>
                  <tbody>
                    {muestra.map((f, i) => (
                      <tr key={i}>
                        <td>{f.empresa}</td>
                        <td>{f.ciudad || '—'}</td>
                        <td>{(f.prioridad || '—').toString().trim().charAt(0).toUpperCase() || '—'}</td>
                        <td className="importar__preview-contacto">{f.contacto || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filasMapeadas.length > muestra.length && (
                  <p className="importar__zona-hint">…y {filasMapeadas.length - muestra.length} más</p>
                )}
              </div>

              <div className="importar__diagnostico">
                {revisando ? (
                  <span className="importar__zona-hint">Revisando duplicados...</span>
                ) : diagnostico ? (
                  <>
                    <span className="importar__chip importar__chip--ok">
                      <IconCheckCircle size={13} /> {aCrear} nuevo{aCrear !== 1 ? 's' : ''}
                    </span>
                    {diagnostico.duplicados.length > 0 && (
                      <span className="importar__chip importar__chip--dup">
                        {diagnostico.duplicados.length} ya existía{diagnostico.duplicados.length !== 1 ? 'n' : ''}
                      </span>
                    )}
                  </>
                ) : null}
              </div>

              {diagnostico && diagnostico.duplicados.length > 0 && (
                <details className="importar__duplicados">
                  <summary>Ver las que no se van a cargar</summary>
                  <ul>
                    {diagnostico.duplicados.map((d, i) => (
                      <li key={i}>
                        <strong>{d.empresa}</strong> — {d.codigo}{d.etapa ? ` · ${d.etapa}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </>
      )}

      {/* ─── Paso 3: el resultado ─── */}
      {resultado && (
        <div className="importar__resultado">
          <IconCheckCircle size={30} />
          <p className="importar__zona-titulo">
            {resultado.creados} prospecto{resultado.creados !== 1 ? 's' : ''} importado{resultado.creados !== 1 ? 's' : ''}
          </p>
          {resultado.duplicados.length > 0 && (
            <p className="importar__zona-hint">
              {resultado.duplicados.length === 1
                ? 'Otra se omitió porque ya estaba cargada.'
                : `${resultado.duplicados.length} se omitieron porque ya estaban cargadas.`}
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
